// src/App.tsx
import * as React from 'react';
const { Suspense, useEffect, useState } = React;
import { Outlet } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from 'next-themes';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import '@/styles/desktop-dialog.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { logger } from '@/utils/logger';
import { AbielAudioProvider } from '@/components/audio/AbielAudioContext';
import { AuthProvider } from '@/contexts/auth';
import { UserProvider } from '@/contexts/UserContext';
import { SettingsProvider } from '@/contexts/settings';
import { LoadingProvider } from '@/contexts/LoadingContext';
import { PrayerChoiceProvider } from '@/contexts/PrayerChoiceContext';
import { SearchProvider } from '@/contexts/SearchContext';
import { LoadingMessage } from '@/components/ui/loading-message';
import GlobalPrayerChoiceController from '@/components/ai-prayer/choice/GlobalPrayerChoiceController';
import { GlobalSearchModal } from '@/components/search/GlobalSearchModal';
import LoadingScreen from '@/components/LoadingScreen';
import { LoadingStateManager } from '@/components/LoadingStateManager';
import { LoadingTimeoutHandler } from '@/components/LoadingTimeoutHandler';
import { NetworkStatusIndicator } from '@/components/NetworkStatusIndicator';
import { SkeletonProvider } from '@/components/skeletons/SkeletonProvider';
import { PageTransition } from '@/components/transitions/PageTransition';
import { ModalProvider } from '@/contexts/ModalContext';
import { usePreloading } from '@/hooks/usePreloading';
import { useLoadingCoordination } from '@/hooks/useLoadingCoordination';
import { useZIndexValidation } from '@/hooks/useZIndexValidation';
import SafeAreaGuard from '@/safe-area/SafeAreaGuard';
import { MobileDetectionProvider, useMobileDetection } from '@/contexts/MobileDetectionContext';
import '@/styles/mobile.css';
import { PkceHandler } from '@/components/PkceHandler';

// Modal pages (rendered, not routed)
import StudyPage from '@/pages/StudyPage';
import ScriptureSearchPage from '@/pages/ScriptureSearchPage';

const queryClient = new QueryClient();

function AppContent() {
  const { isMobile } = useMobileDetection();

  usePreloading({ preloadRoutes: true, preloadAudio: true, preloadBible: true, preloadPrayers: true });
  const { getCoordinatedMessage } = useLoadingCoordination();
  useZIndexValidation();

  // ---- Modal state ---------------------------------------------------------
  const [studyOpen, setStudyOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [studySeed, setStudySeed] = useState<string | null>(null);
  const [prevUrl, setPrevUrl] = useState<string | null>(null);
  // --------------------------------------------------------------------------

  // Global handlers used by VerseOfDay, etc.
  useEffect(() => {
    (window as any).__ABIEL_HANDLERS__ = {
      openStudy: (prefill: string) => {
        setPrevUrl(window.location.pathname + window.location.search);
        setStudySeed(prefill || '');
        setStudyOpen(true);
      },
      openBibleSearch: (reference: string, text: string) => {
        setPrevUrl(window.location.pathname + window.location.search);
        const url = new URL(window.location.href);
        url.searchParams.set('query', reference || '');
        window.history.replaceState({}, '', url.toString());
        (window as any).__ABIEL_SEARCH_SEED__ = { ref: reference, text };
        setSearchOpen(true);
      },
      openBibleRead: (_reference: string) => {
        // optional: implement a reader modal here later
      },
    };
  }, []);

  // Close helpers restore URL
  const closeStudy = () => {
    setStudyOpen(false);
    if (prevUrl) { window.history.replaceState({}, '', prevUrl); setPrevUrl(null); }
  };
  const closeSearch = () => {
    setSearchOpen(false);
    if (prevUrl) { window.history.replaceState({}, '', prevUrl); setPrevUrl(null); }
  };

  // ESC closes whichever modal is open
  useEffect(() => {
    if (!studyOpen && !searchOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (studyOpen) closeStudy();
        else if (searchOpen) closeSearch();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [studyOpen, searchOpen]);

  // iOS platform diagnostics (unchanged)
  useEffect(() => {
    const platform = Capacitor.getPlatform();
    const isIOS = platform === 'ios';
    if (isIOS) {
      const apiKey = import.meta.env.VITE_REVENUECAT_PUBLIC_API_KEY;
      if (apiKey) {
        import('@/lib/plugins/revenuecat').then(({ ensureConfigured }) => {
          ensureConfigured(apiKey).catch((e) => logger.warn('[App] RevenueCat config failed:', e));
        });
      }
      if (import.meta.env.DEV) {
        logger.log('Platform:', platform);
        logger.log('Capacitor native platform:', Capacitor.isNativePlatform());
        logger.log('User agent:', navigator.userAgent);
        logger.log('Screen dimensions:', { width: window.screen.width, height: window.screen.height, devicePixelRatio: window.devicePixelRatio });
        try {
          const css = window.getComputedStyle(document.documentElement);
          logger.log('Safe Area Insets:', {
            top: css.getPropertyValue('--safe-area-inset-top'),
            bottom: css.getPropertyValue('--safe-area-inset-bottom'),
            left: css.getPropertyValue('--safe-area-inset-left'),
            right: css.getPropertyValue('--safe-area-inset-right'),
          });
        } catch { /* noop */ }
        try { CapApp.getInfo().then(info => logger.log('Capacitor App Info:', info)); } catch { /* noop */ }
      }
    }
  }, []);

  // A small header used on mobile full-screen modals
  const MobileModalHeader: React.FC<{ title: string; onClose: () => void }> = ({ title, onClose }) => (
    <div
      className="sticky top-0 z-[2] flex items-center justify-between border-b bg-background/95 px-3 py-2
                 supports-[backdrop-filter]:backdrop-blur"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="text-sm font-medium">{title}</div>
      <button
        type="button"
        onClick={onClose}
        className="inline-flex h-9 items-center rounded-md border px-3 text-sm"
        aria-label="Close"
      >
        Close
      </button>
    </div>
  );

  return (
    <LoadingTimeoutHandler timeout={45000}>
      <PageTransition>
        <SafeAreaGuard />
        <div className="min-h-screen bg-background">
          <Suspense fallback={<LoadingScreen message={getCoordinatedMessage('Loading application...')} />}>
            <Outlet />
          </Suspense>

          {/* === Study modal (full-screen on mobile, centered card on desktop) === */}
          {studyOpen && (
            <div
              className="fixed inset-0 z-[1000] bg-black/50"
              role="presentation"
              onClick={isMobile ? undefined : closeStudy} // click-away on desktop card backdrop
            >
              {isMobile ? (
                <div
                  role="dialog"
                  aria-modal="true"
                  className="fixed inset-0 z-[1001] flex h-[100dvh] w-screen flex-col bg-background"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MobileModalHeader title="Bible Study" onClose={closeStudy} />
                  <div className="flex-1 min-h-0">
                    <Suspense fallback={<LoadingScreen message="Opening study..." />}>
                      <StudyPage seed={studySeed ?? ''} />
                    </Suspense>
                  </div>
                </div>
              ) : (
                <div
                  role="dialog"
                  aria-modal="true"
                  className="mx-auto mt-10 w-[calc(100%-2rem)] max-w-3xl rounded-xl bg-background shadow-xl flex flex-col max-h-[85vh]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex-1 min-h-0 p-4">
                    <Suspense fallback={<LoadingScreen message="Opening study..." />}>
                      <StudyPage seed={studySeed ?? ''} />
                    </Suspense>
                  </div>
                  <div className="border-t p-3 text-right">
                    <button className="inline-flex h-9 items-center rounded-md border px-3 text-sm" onClick={closeStudy}>
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* === Scripture Search modal (full-screen on mobile, centered card on desktop) === */}
          {searchOpen && (
            <div
              className="fixed inset-0 z-[1000] bg-black/50"
              role="presentation"
              onClick={isMobile ? undefined : closeSearch}
            >
              {isMobile ? (
                <div
                  role="dialog"
                  aria-modal="true"
                  className="fixed inset-0 z-[1001] flex h-[100dvh] w-screen flex-col bg-background"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MobileModalHeader title="Scripture Search" onClose={closeSearch} />
                  <div className="flex-1 min-h-0">
                    <Suspense fallback={<LoadingScreen message="Opening search..." />}>
                      <ScriptureSearchPage />
                    </Suspense>
                  </div>
                </div>
              ) : (
                <div
                  role="dialog"
                  aria-modal="true"
                  className="mx-auto mt-10 w-[calc(100%-2rem)] max-w-3xl rounded-xl bg-background shadow-xl flex flex-col max-h-[85vh]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex-1 min-h-0 p-4">
                    <Suspense fallback={<LoadingScreen message="Opening search..." />}>
                      <ScriptureSearchPage />
                    </Suspense>
                  </div>
                  <div className="border-t p-3 text-right">
                    <button className="inline-flex h-9 items-center rounded-md border px-3 text-sm" onClick={closeSearch}>
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Global UI */}
          <Toaster />
          <LoadingMessage variant="toast" />
          <NetworkStatusIndicator />
          <GlobalPrayerChoiceController />
          <GlobalSearchModal />
        </div>
      </PageTransition>
    </LoadingTimeoutHandler>
  );
}

function App() {
  return (
    <MobileDetectionProvider>
      <AuthProvider>
        <UserProvider>
          <SettingsProvider>
            <QueryClientProvider client={queryClient}>
              <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
                <LoadingProvider>
                  <LoadingStateManager>
                    <SkeletonProvider>
                      <ModalProvider>
                        <PrayerChoiceProvider>
                          <SearchProvider>
                            <AbielAudioProvider>
                              <AppContent />
                            </AbielAudioProvider>
                          </SearchProvider>
                        </PrayerChoiceProvider>
                      </ModalProvider>
                    </SkeletonProvider>
                  </LoadingStateManager>
                </LoadingProvider>
              </ThemeProvider>
            </QueryClientProvider>
          </SettingsProvider>
        </UserProvider>
      </AuthProvider>
    </MobileDetectionProvider>
  );
}

export default App;
